import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Small pill that renders a count badge.
 * Colors: red (default, chat/notifications), green (whatsapp).
 */
const Badge = ({ count, color = 'red', max = 99, testId }) => {
  if (!count || count <= 0) return null;
  const bg = color === 'green' ? 'bg-green-600' : 'bg-red-500';
  return (
    <span
      className={`absolute right-3 ${bg} text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 flex items-center justify-center px-1`}
      data-testid={testId}
    >
      {count > max ? `${max}+` : count}
    </span>
  );
};

/**
 * Collapsible sidebar group (admin + parent). Auto-expands when a child is active.
 */
export const NavGroup = ({ group, location, onItemClick, waBadgeCount, chatBadgeCount }) => {
  const hasActiveItem = group.items?.some((item) => {
    const [itemPathname, itemSearch] = (item.path || '').split('?');
    return itemSearch
      ? location.pathname + location.search === item.path
      : location.pathname === itemPathname && !location.search;
  });
  const [isOpen, setIsOpen] = useState(hasActiveItem);

  useEffect(() => {
    if (hasActiveItem) setIsOpen(true);
  }, [hasActiveItem]);

  const Icon = group.icon;

  return (
    <div className="mb-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-4 py-2.5 rounded-md transition-colors duration-200
          ${hasActiveItem
            ? 'bg-theme-primary/10 text-theme-primary'
            : 'hover:bg-theme-surface hover:text-theme-text text-theme-muted'}
        `}
        data-testid={`nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <span className="font-medium text-sm">{group.label}</span>
        </div>
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {isOpen && (
        <div className="mt-1 ml-4 pl-3 border-l border-slate-700 space-y-0.5">
          {group.items.map((item) => {
            const ItemIcon = item.icon;
            const [itemPathname, itemSearch] = (item.path || '').split('?');
            const isActive = itemSearch
              ? location.pathname + location.search === item.path
              : location.pathname === itemPathname && !location.search;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onItemClick}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-200 text-sm relative
                  ${isActive
                    ? 'bg-theme-primary/10 text-theme-primary'
                    : 'hover:bg-theme-surface hover:text-theme-text text-theme-muted'}
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <ItemIcon className="h-4 w-4" />
                <span>{item.label}</span>
                {item.showWaBadge && (
                  <Badge count={waBadgeCount} color="green" testId="whatsapp-unread-badge" />
                )}
                {item.showChatBadge && <Badge count={chatBadgeCount} />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Top-level sidebar link (single item, not grouped).
 * Supports notification / chat / whatsapp badges on the right.
 */
export const NavItem = ({
  item,
  location,
  onItemClick,
  notificationCount,
  chatBadgeCount,
  waBadgeCount,
  big = true, // true for top-level (py-2.5), false for nested
}) => {
  const Icon = item.icon;
  // item.path may carry a query string (e.g. "/parent/tournaments?tab=schedule"
  // for the Games shortcut). Match full URL when present so we don't highlight
  // two siblings at once.
  const [itemPathname, itemSearch] = (item.path || '').split('?');
  const isActive = itemSearch
    ? location.pathname + location.search === item.path
    : location.pathname === itemPathname && !location.search;

  return (
    <Link
      to={item.path}
      onClick={onItemClick}
      className={`
        flex items-center gap-3 px-4 ${big ? 'py-2.5' : 'py-2'} rounded-md transition-colors duration-200 relative
        ${isActive
          ? 'bg-theme-primary/10 text-theme-primary border-r-2 border-theme-primary'
          : 'hover:bg-theme-surface hover:text-theme-text text-theme-muted'}
      `}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium text-sm">{item.label}</span>
      {item.showBadge && <Badge count={notificationCount} max={9} />}
      {item.showChatBadge && <Badge count={chatBadgeCount} />}
      {item.showWaBadge && (
        <Badge count={waBadgeCount} color="green" testId="whatsapp-unread-badge" />
      )}
    </Link>
  );
};
